/**
 * Content Anim Manager - Moon Moon Agency
 * Slider domaines avec animations GSAP, SplitType et CustomEase
 * Supporte plusieurs instances de la section sur la même page.
 */

/*********************************************************************************************
 * ⚙️ CONFIG
 */
const MMContentAnimManager = {

  config: {
    COPIES:   9,
    NAV_DUR:  0.7,
    NAV_EASE: 'cubic-bezier(0.7, 0, 0.15, 1)',

    eases: {
      hop:           '0.87, 0, 0.13, 1',
      slideshowWipe: '0.6, 0.08, 0.02, 0.99',
      cardsExit:     '0.66, 0.03, 0.75, 0.63',
    },

    elem: {
      title: {
        out: { ease: 'power4.inOut', duration: 0.8, stagger: 0.2, delay: 0 },
        in:  { ease: 'hop',          duration: 0.6, stagger: 0.2, delay: 0 },
      },
      desc: {
        out: { ease: 'power4.inOut', duration: 0.8, stagger: 0.2, delay: 0.08 },
        in:  { ease: 'power4.out',   duration: 0.6, stagger: 0.2, delay: 0.07 },
      },
      cta: {
        out: { ease: 'power4.inOut', duration: 0.5, delay: 0.16 },
        in:  { ease: 'power4.inOut', duration: 0.5, delay: 0.16 },
      },
      cards: {
        out: { ease: 'cards-exit', duration: 0.6, stagger: 0.05, delay: 0.25, travel: 120 },
        in:  { ease: 'power4.out', duration: 0.9, stagger: 0.05, delay: 0.25, travel: 120 },
      },
      image: { ease: 'slideshow-wipe', duration: 0.9, parallax: 30 },
    },

    timing: { imageDelay: 0.6, inDelay: 0.65 },
  },

  /*********************************************************************************************
   * 🚀 INIT
   */
  init() {
    const data = window.mmContentAnimData;
    if (!data || !window.gsap || !window.SplitType || !window.CustomEase) {
      window.log?.('warn', '🎬 [CONTENT_ANIM]', 'Dépendances manquantes (gsap / SplitType / CustomEase)');
      return;
    }

    gsap.registerPlugin(CustomEase);
    CustomEase.create('hop',            this.config.eases.hop);
    CustomEase.create('slideshow-wipe', this.config.eases.slideshowWipe);
    CustomEase.create('cards-exit',     this.config.eases.cardsExit);

    const sectionIds = Object.keys(data);
    sectionIds.forEach(sid => this._initSection(sid, data[sid].slides));
    window.log?.('info', '🎬 [CONTENT_ANIM]', `${sectionIds.length} section(s) initialisée(s)`);
  },

  /*********************************************************************************************
   * 🔧 SECTION INSTANCE
   */
  _initSection(sid, slidesData) {
    const N = slidesData?.length;
    if (!N) return;

    const C      = this.config;
    const E      = C.elem;
    const T      = C.timing;
    const COPIES = C.COPIES;
    const CENTER = Math.floor(COPIES / 2);

    /* ---- DOM ---- */
    const getEl = id => document.getElementById(`mm-ca-${id}-${sid}`);
    const media      = getEl('media');
    const slidesWrap = getEl('slides');
    const navTrack        = getEl('nav-track');
    const navView         = getEl('nav-names');
    const mobileNavTrack  = getEl('m-nav-track');
    const mobileNavView   = getEl('m-nav-names');
    const mobileTitle     = getEl('m-title');
    if (!media || !slidesWrap || !navTrack || !navView) return;

    const slides = gsap.utils.toArray('.mm-content-anim__slide', slidesWrap);

    /* ---- État mutable ---- */
    const splits         = [];
    const navItems       = [];
    const mobileNavItems = [];
    let navIndex    = CENTER * N;
    let navActive   = null;
    let mNavActive  = null;
    let current     = 0;
    let isAnimating = false;

    /* ---- Préchargement images ---- */
    const imageUrlFor = (s) => (window.innerWidth <= 990 && s?.imageUrlMobile) || s?.imageUrl;
    const preloadObserver = new IntersectionObserver((entries, observer) => {
      if (!entries.some(e => e.isIntersecting)) return;
      observer.disconnect();
      slidesData.forEach(s => { const url = imageUrlFor(s); if (url) { const img = new Image(); img.src = url; } });
    }, { rootMargin: '200px' });
    preloadObserver.observe(media);

    /* ---- Helpers ---- */

    function createSplit(el) {
      if (!el) return null;
      const split = new SplitType(el, { types: 'lines' });
      split.lines.forEach(line => {
        const mask = document.createElement('div');
        mask.className = 'mm-overflow-hidden mm-block';
        el.insertBefore(mask, line);
        mask.appendChild(line);
      });
      return split;
    }

    function goTo(toIndex, dir) {
      if (isAnimating || toIndex === current || !splits[current]) return;
      isAnimating = true;
      dir = dir || (toIndex > current ? 1 : -1);

      /* Mobile: image clip-path transition + instant content swap */
      if (window.innerWidth <= 990) {
        const mOutSlide = slides[current];
        const mInSlide  = slides[toIndex];

        const layer = document.createElement('div');
        layer.className = 'mm-content-anim__media-layer mm-media-banner mm-overflow-hidden';
        if (slidesData[toIndex]?.imageUrl) {
          const img   = document.createElement('img');
          img.src     = imageUrlFor(slidesData[toIndex]);
          img.alt     = '';
          img.loading = 'lazy';
          layer.appendChild(img);
        }
        gsap.set(layer, { clipPath: 'inset(100% 0 0 0)' });
        media.appendChild(layer);

        gsap.to(layer, {
          clipPath: 'inset(0% 0 0 0)',
          duration: E.image.duration,
          ease: E.image.ease,
          onComplete() {
            const layers = media.querySelectorAll('.mm-content-anim__media-layer');
            layers.forEach((l, i) => { if (i < layers.length - 1) l.remove(); });
            isAnimating = false;
          },
        });

        mOutSlide.classList.remove('is-active');
        mInSlide.classList.add('is-active');
        current = toIndex;
        updateMobileTitle(current);
        requestAnimationFrame(() => initSlideCards(mInSlide));
        return;
      }

      const out      = splits[current];
      const inn      = splits[toIndex];
      const outSlide = slides[current];
      const inSlide  = slides[toIndex];

      /* Nouveau calque image — wipe ascendant via clip-path (image à taille finale dès le départ) */
      const layer = document.createElement('div');
      layer.className = 'mm-content-anim__media-layer mm-media-banner mm-overflow-hidden';

      if (slidesData[toIndex]?.imageUrl) {
        const img   = document.createElement('img');
        img.src     = slidesData[toIndex].imageUrl;
        img.alt     = '';
        img.loading = 'lazy';
        layer.appendChild(img);
      }

      gsap.set(layer, { clipPath: 'inset(100% 0 0 0)' });
      media.appendChild(layer);
      inSlide.dataset.incoming = '1';

      const tl = gsap.timeline({
        defaults: { ease: 'power4.inOut' },
        onComplete() {
          delete inSlide.dataset.incoming;
          const layers = media.querySelectorAll('.mm-content-anim__media-layer');
          layers.forEach((l, i) => { if (i < layers.length - 1) l.remove(); });
          outSlide.classList.remove('is-active');
          inSlide.classList.add('is-active');
          gsap.set(outSlide, { clearProps: 'visibility' });
          current     = toIndex;
          isAnimating = false;
          updateMobileTitle(current);
        },
      });

      /* Phase sortante */
      if (out.title) tl.to(out.title.lines, { yPercent: -110, duration: E.title.out.duration, ease: E.title.out.ease, stagger: { amount: E.title.out.stagger } }, E.title.out.delay);
      if (out.desc)  tl.to(out.desc.lines,  { yPercent: -110, duration: E.desc.out.duration,  ease: E.desc.out.ease,  stagger: { amount: E.desc.out.stagger  } }, E.desc.out.delay);
      if (out.cta)   tl.to(out.cta, { autoAlpha: 0, duration: E.cta.out.duration, ease: E.cta.out.ease }, E.cta.out.delay);
      if (out.cards.length) tl.to(out.cards, { autoAlpha: 0, y: -E.cards.out.travel, duration: E.cards.out.duration, ease: E.cards.out.ease, stagger: E.cards.out.stagger }, E.cards.out.delay);

      /* Wipe image — clip-path révèle de bas en haut sans modifier la taille de l'image */
      const imgAt = T.imageDelay;
      tl.to(layer, { clipPath: 'inset(0% 0 0 0)', duration: E.image.duration, ease: E.image.ease }, imgAt);

      /* Phase entrante */
      const inAt = imgAt + T.inDelay;
      tl.set(inSlide, { visibility: 'visible' }, inAt);
      if (inn.title) tl.fromTo(inn.title.lines, { yPercent: 110 }, { yPercent: 0, duration: E.title.in.duration, ease: E.title.in.ease, stagger: { amount: E.title.in.stagger }, immediateRender: false }, inAt + E.title.in.delay);
      if (inn.desc)  tl.fromTo(inn.desc.lines,  { yPercent: 110 }, { yPercent: 0, duration: E.desc.in.duration,  ease: E.desc.in.ease,  stagger: { amount: E.desc.in.stagger  }, immediateRender: false }, inAt + E.desc.in.delay);
      if (inn.cta)   tl.fromTo(inn.cta, { autoAlpha: 0 }, { autoAlpha: 1, duration: E.cta.in.duration, ease: E.cta.in.ease }, inAt + E.cta.in.delay);
      if (inn.cards.length) tl.fromTo(inn.cards, { autoAlpha: 0, y: E.cards.in.travel }, { autoAlpha: 1, y: 0, duration: E.cards.in.duration, ease: E.cards.in.ease, stagger: E.cards.in.stagger }, inAt + E.cards.in.delay);
    }

    function makeNavBtn(name, abs) {
      const btn = document.createElement('button');
      btn.className   = 'mm-content-anim__nav-name mm-no-wrap mm-text-small-medium mm-uppercase';
      btn.textContent = name;
      btn.type        = 'button';
      btn.addEventListener('click', () => {
        const dir = Math.sign(abs - navIndex) || 1;
        navigateTo(abs, dir);
      });
      return btn;
    }

    function buildNav() {
      navTrack.innerHTML = '';
      navItems.length    = 0;
      if (mobileNavTrack) { mobileNavTrack.innerHTML = ''; mobileNavItems.length = 0; }

      for (let c = 0; c < COPIES; c++) {
        slidesData.forEach((s, i) => {
          const abs = c * N + i;

          const btn = makeNavBtn(s.name, abs);
          navTrack.appendChild(btn);
          navItems[abs] = btn;

          if (mobileNavTrack) {
            const mBtn = makeNavBtn(s.name, abs);
            mobileNavTrack.appendChild(mBtn);
            mobileNavItems[abs] = mBtn;
          }
        });
      }

      navIndex = CENTER * N + current;
      requestAnimationFrame(() => { centreNav(); centreMobileNav(); });
      document.fonts?.ready.then(() => { centreNav(); centreMobileNav(); });
    }

    function offsetFor(abs) {
      const item = navItems[abs];
      return navView.clientWidth / 2 - (item.offsetLeft + item.offsetWidth / 2);
    }

    function mOffsetFor(abs) {
      const item = mobileNavItems[abs];
      if (!item || !mobileNavView) return 0;
      return mobileNavView.clientWidth / 2 - (item.offsetLeft + item.offsetWidth / 2);
    }

    function setTrackX(x, animate) {
      navTrack.style.transition = animate ? `transform ${C.NAV_DUR}s ${C.NAV_EASE}` : 'none';
      navTrack.style.transform  = `translateX(${x}px)`;
    }

    function setMobileTrackX(x, animate) {
      if (!mobileNavTrack) return;
      mobileNavTrack.style.transition = animate ? `transform ${C.NAV_DUR}s ${C.NAV_EASE}` : 'none';
      mobileNavTrack.style.transform  = `translateX(${x}px)`;
    }

    function setNavActive() {
      navActive?.classList.remove('is-active');
      navActive = navItems[navIndex];
      navActive?.classList.add('is-active');
      mNavActive?.classList.remove('is-active');
      mNavActive = mobileNavItems[navIndex];
      mNavActive?.classList.add('is-active');
    }

    function centreNav() {
      setTrackX(offsetFor(navIndex), false);
      setNavActive();
    }

    function centreMobileNav() {
      if (!mobileNavTrack || !mobileNavView) return;
      setMobileTrackX(mOffsetFor(navIndex), false);
    }

    function updateMobileTitle(idx) {
      if (mobileTitle) mobileTitle.textContent = slidesData[idx]?.title || '';
    }

    function initSlideCards(slide) {
      if (!window.Splide || window.innerWidth > 990) return;
      const cardsEl = slide.querySelector('.mm-content-anim__slide-cards');
      if (!cardsEl || cardsEl._splide) return;
      const cards = Array.from(cardsEl.children);
      if (cards.length < 1) return;

      const track = document.createElement('div');
      track.className = 'splide__track';
      const list = document.createElement('ul');
      list.className = 'splide__list';
      cards.forEach(card => {
        const li = document.createElement('li');
        li.className = 'splide__slide';
        li.appendChild(card);
        list.appendChild(li);
      });
      track.appendChild(list);
      cardsEl.innerHTML = '';
      cardsEl.appendChild(track);
      cardsEl.classList.add('splide');

      cardsEl._splide = new window.Splide(cardsEl, {
        fixedWidth: '160px',
        gap: '8px',
        padding: { left: '16px', right: '16px' },
        arrows: false,
        pagination: false,
      }).mount();
    }

    function navigateTo(toAbs, dir) {
      if (isAnimating) return;
      const toLogical = ((toAbs % N) + N) % N;
      if (toLogical === current) return;
      navIndex = toAbs;
      setTrackX(offsetFor(navIndex), true);
      setMobileTrackX(mOffsetFor(navIndex), true);
      setNavActive();
      goTo(toLogical, dir);
    }

    function navStep(dir) { navigateTo(navIndex + dir, dir); }

    /* ---- Setup slides ---- */
    slides.forEach((slide, i) => {
      const cta = slide.querySelector('.mm-content-anim__slide-cta');
      if (cta) {
        const wrap = document.createElement('div');
        wrap.className = 'mm-overflow-hidden';
        cta.parentNode.insertBefore(wrap, cta);
        wrap.appendChild(cta);
      }

      const cards = gsap.utils.toArray('.mm-content-anim__card', slide);
      splits[i] = {
        title: createSplit(slide.querySelector('.mm-content-anim__slide-title')),
        desc:  createSplit(slide.querySelector('.mm-content-anim__slide-desc')),
        cta,
        cards,
      };

      if (i !== current && window.innerWidth > 990) {
        if (splits[i].title) gsap.set(splits[i].title.lines, { yPercent: 110 });
        if (splits[i].desc)  gsap.set(splits[i].desc.lines,  { yPercent: 110 });
        if (cta)             gsap.set(cta, { autoAlpha: 0 });
        gsap.set(cards, { autoAlpha: 0, y: E.cards.in.travel });
      }
    });

    /* ---- Nav ---- */
    buildNav();
    updateMobileTitle(current);
    initSlideCards(slides[current]);

    /* ---- Événements ---- */
    getEl('nav-next')?.addEventListener('click', () => navStep(1));
    getEl('nav-prev')?.addEventListener('click', () => navStep(-1));
    getEl('m-next')?.addEventListener('click',   () => navStep(1));
    getEl('m-prev')?.addEventListener('click',   () => navStep(-1));

    navTrack.addEventListener('transitionend', e => {
      if (e.propertyName !== 'transform') return;
      const mod    = ((navIndex % N) + N) % N;
      const target = CENTER * N + mod;
      if (target !== navIndex) { navIndex = target; centreNav(); centreMobileNav(); }
    });

    if (mobileNavTrack) {
      mobileNavTrack.addEventListener('transitionend', e => {
        if (e.propertyName !== 'transform') return;
        const mod    = ((navIndex % N) + N) % N;
        const target = CENTER * N + mod;
        if (target !== navIndex) { navIndex = target; centreNav(); centreMobileNav(); }
      });
    }

    window.addEventListener('resize', () => { centreNav(); centreMobileNav(); });
  },
};

/*********************************************************************************************
 * 🔌 AUTO-INIT
 * GSAP est chargé dynamiquement par scripts.liquid après DOMContentLoaded (event mm:animations:ready).
 * Si déjà disponible (ex: autre section déjà chargée), on init directement.
 */
if (window.gsap && window.SplitType && window.CustomEase) {
  MMContentAnimManager.init();
} else {
  document.addEventListener('mm:animations:ready', () => MMContentAnimManager.init(), { once: true });
}
